import React, { useRef, useEffect, forwardRef, CSSProperties } from 'react';
import { Text, Transformer } from 'react-konva';
import Konva from 'konva';
import { TextElement } from '../types/TextElement';
import { Html } from 'react-konva-utils';
import { Rect } from "react-konva";

interface KonvaTextItemProps {
    textElement: TextElement;
    isSelected: boolean;
    isEditing: boolean;
    onTextChange: (id: string, changes: Partial<TextElement>) => void;
    onSetEditingText: (active: boolean) => void;
    onSelect: (e: Konva.KonvaEventObject<MouseEvent | TouchEvent | Event>) => void;
    onDragEnd: (id: string, newPos: { x: number; y: number }) => void;
    onDragMove?: (id: string, pos: { x: number; y: number; width: number; height: number }) => { x: number; y: number } | void;
}

const KonvaTextItem = forwardRef<Konva.Text, KonvaTextItemProps>(({
    textElement,
    isSelected,
    isEditing,
    onSelect,
    onDragEnd,
    onDragMove,
    onTextChange,
    onSetEditingText,
}, ref) => {

    const textRef = useRef<Konva.Text>(null);
    const transformerRef = useRef<Konva.Transformer>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [isHovered, setIsHovered] = React.useState(false);
    const [hoverRect, setHoverRect] = React.useState<Konva.RectConfig | null>(null);
    const editTextRef = useRef(textElement.text);

    React.useEffect(() => {
        if (isHovered && textRef.current) {
            const box = textRef.current.getClientRect();
            setHoverRect({
                x: box.x,
                y: box.y,
                width: box.width,
                height: box.height,
            });
        } else {
            setHoverRect(null);
        }
    }, [isHovered, textElement]); // recalc on hover or text changes
    

    function getRGBA(hex: string, opacity: number): string {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        const a = Math.min(1, Math.max(0, opacity));  // clamp 0-1
        return `rgba(${r},${g},${b},${a})`;
    }


    useEffect(() => {
        if (isSelected && transformerRef.current && textRef.current) {
            transformerRef.current.nodes([textRef.current]);
            const layer = transformerRef.current.getLayer();
            if (layer) {
                layer.batchDraw();
            }
        }
    }, [isSelected]);

    const finishEditing = () => {
        onSetEditingText(false);
    };





    const getTextareaStyle = (): CSSProperties => {
        if (!textRef.current) return {};

        const node = textRef.current;
        const abs = node.absolutePosition();

        return {
            position: "absolute",
            top: abs.y + "px",
            left: abs.x + "px",

            width: node.width() + "px",
            height: node.height() + "px",

            fontSize: textElement.fontSize,
            fontFamily: textElement.fontFamily,
            lineHeight: `${textElement.fontSize}px`,
            textAlign: textElement.align || "left",


            padding: 0,
            margin: 0,
            border: "1px solid gray",
            boxSizing: "border-box",

            whiteSpace: "pre-wrap",
            overflow: "hidden",
            resize: "none",

            background: "none",
            color: textElement.fill,
            outline: "none",

            transformOrigin: "top left",
            transform: `rotate(${node.rotation()}deg)`,
        };
    };
    return (
        <>
            <Text
                id={textElement.id}
                ref={ref || textRef}
                x={textElement.x}
                y={textElement.y}
                text={textElement.text}
                fontSize={textElement.fontSize}
                fontFamily={textElement.fontFamily}
                fontStyle={`${textElement.fontWeight === 'bold' ? 'bold' : ''} ${textElement.fontStyle === 'italic' ? 'italic' : ''}`.trim() || 'normal'}
                fontWeight={textElement.fontWeight}
                letterSpacing={textElement.letterSpacing ?? 0}
                fill={textElement.fill}
                lineHeight={textElement.lineHeight ?? 1}
                align={textElement.align}
                width={textElement.width}
                rotation={textElement.rotation}
                opacity={textElement.opacity ?? 1}            
                draggable
                onClick={onSelect}
                onTap={onSelect}
                onDragStart={onSelect}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                visible={!isEditing}
                stroke={textElement.strokeColor}
                strokeWidth={textElement.strokeWidth}
                perfectDrawEnabled={!(textElement.strokeWidth || textElement.shadowBlur)}// this fix the crash on the app
                shadowColor={
                    (textElement.shadowBlur ?? 0) > 0
                        ? getRGBA(textElement.shadowColor || '#000000', textElement.shadowOpacity ?? 1)
                        : 'transparent'
                }
                shadowBlur={textElement.shadowBlur ?? 0}
                shadowOffset={{
                    x: textElement.shadowOffsetX ?? 0,
                    y: textElement.shadowOffsetY ?? 0,
                }}
                onDragMove={(e) => {
                    if (onDragMove) {
                        const id = textElement.id;
                        const x = e.target.x();
                        const y = e.target.y();
                        const box = textRef.current?.getClientRect();
                        const width = box?.width ?? textElement.width ?? 100;
                        const height = box?.height ?? (textElement.fontSize ?? 20) * 1.2;

                        const snappedPos = onDragMove(id, { x, y, width, height });
                        if (snappedPos) {
                            e.target.x(snappedPos.x);
                            e.target.y(snappedPos.y);
                        }
                    }
                }}
                onDragEnd={(e) => onDragEnd(textElement.id, { x: e.target.x(), y: e.target.y() })}
                onDblClick={() => onSetEditingText(true)}
                onDblTap={() => onSetEditingText(true)}
                onTransformEnd={() => {
                    const node = textRef.current;
                    if (node) {
                        const scaleX = node.scaleX();
                        const scaleY = node.scaleY();

                        onTextChange(textElement.id, {
                            x: node.x(),
                            y: node.y(),
                            rotation: node.rotation(),
                            width: Math.max(30, node.width() * scaleX),
                            fontSize: Math.max(8, textElement.fontSize * scaleY),
                        });

                        node.scaleX(1);
                        node.scaleY(1);
                    }
                }}
            />

            {/* Hover rectangle - blue outline without handles */}
            {hoverRect && !isSelected && (
                <Rect
                    x={hoverRect.x}
                    y={hoverRect.y}
                    width={hoverRect.width}
                    height={hoverRect.height}
                    stroke="#59b2d8"
                    strokeWidth={2}
                    listening={false}
                    perfectDrawEnabled={false}
                    cornerRadius={0}
                />
            )}

            {isSelected && !isEditing && (
                <Transformer
                    ref={transformerRef}
                    rotateEnabled
                    enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right', 'middle-left', 'middle-right']}
                    boundBoxFunc={(oldBox, newBox) => (newBox.width < 30 || newBox.height < 15 ? oldBox : newBox)}
                    rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
                    rotationSnapTolerance={8}
                    borderStroke="#59b2d8"
                    borderStrokeWidth={2}
                    anchorSize={12}
                    anchorStrokeWidth={2}
                    anchorCornerRadius={1}
                    rotateAnchorOffset={32}
                    anchorStyleFunc={(anchor) => {
                        // make rotate node circle
                        if (anchor.hasName('rotater')) {
                            anchor.scale({ x: 1.5, y: 1.5 });
                            anchor.cornerRadius(50);
                            anchor.strokeWidth(1.5);
                        }

                    }}
                />
            )}

            {isEditing && textRef.current && (
                <Html>
                    <textarea
                        ref={textareaRef}
                        key={`${textElement.id}-${textElement.fontFamily}`}
                        style={getTextareaStyle()}
                        defaultValue={textElement.text}
                        autoFocus
                        onChange={(e) => {
                            editTextRef.current = e.target.value;
                        }}
                        onBlur={() => {
                            onTextChange(textElement.id, {
                                text: editTextRef.current,
                            });
                            finishEditing();
                        }}
                        onInput={() => {
                            if (textareaRef.current) {
                                textareaRef.current.style.height = "auto";
                                textareaRef.current.style.height =
                                    textareaRef.current.scrollHeight + "px";
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") {
                                e.preventDefault();
                                finishEditing();
                            }

                            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                                e.preventDefault();
                                finishEditing();
                            }
                        }}
                    />
                </Html>
            )}
        </>
    );
});

export default KonvaTextItem;
